import { useTranslation } from "react-i18next";

import { WFPButton } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { MultiSelectCombobox } from "../Form/Components/MultiSelectCombobox";
import type { EntityId } from "../../types";

export interface SubjectFilterValues {
  status: string;
  content_type: string;
  topic: EntityId[];
}

interface Option {
  value: string;
  label: string;
}

interface TopicOption {
  value: EntityId;
  label: string;
}

interface FiltersProps {
  filters: SubjectFilterValues;
  onChange: (patch: Partial<SubjectFilterValues>) => void;
  onClear: () => void;
  types: Option[];
  topics: TopicOption[];
}

const SingleSelectFilter = ({
  id,
  label,
  placeholder,
  value,
  options,
  onValueChange,
}: {
  id: string;
  label: string;
  placeholder: string;
  value: string;
  options: Option[];
  onValueChange: (value: string) => void;
}) => (
  <div className="flex w-56 flex-col gap-1">
    <Label htmlFor={id} className="font-semibold text-foreground text-sm">
      {label}
    </Label>
    <Select value={value || undefined} onValueChange={onValueChange}>
      <SelectTrigger id={id} className="h-10 w-full border-border">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent position="popper" className="max-h-72">
        {options.map(option => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

export const Filters = ({ filters, onChange, onClear, types, topics }: FiltersProps) => {
  const { t } = useTranslation();

  const statusOptions: Option[] = [
    { value: "published", label: t("infoHub.filters.published") },
    { value: "draft", label: t("infoHub.filters.draft") },
    { value: "withdrawn", label: t("infoHub.filters.withdrawn") },
  ];

  const selectedTopics = topics.filter(topic => filters.topic.includes(topic.value));

  return (
    <div className="flex flex-wrap items-end gap-4">
      <SingleSelectFilter
        id="filter-status"
        label={t("infoHub.filters.status")}
        placeholder={t("common.select")}
        value={filters.status}
        options={statusOptions}
        onValueChange={value => onChange({ status: value })}
      />
      <SingleSelectFilter
        id="filter-content-type"
        label={t("infoHub.filters.contentType")}
        placeholder={t("common.select")}
        value={filters.content_type}
        options={types}
        onValueChange={value => onChange({ content_type: value })}
      />
      <div className="flex w-72 flex-col gap-1">
        <Label htmlFor="filter-topic" className="font-semibold text-foreground text-sm">
          {t("infoHub.filters.topic")}
        </Label>
        <MultiSelectCombobox<TopicOption>
          id="filter-topic"
          options={topics}
          value={selectedTopics}
          onChange={selected => onChange({ topic: selected.map(option => option.value) })}
          placeholder={t("common.select")}
        />
      </div>
      <WFPButton type="button" variant="link" onClick={onClear}>
        {t("filters.clearAll")}
      </WFPButton>
    </div>
  );
};

export default Filters;
